import { useCallback, useEffect, useRef } from "react";

const useThrottledCallback = <T extends (...args: any[]) => any>(callback: T, delay = 1000): T => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  const isLockedRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      if (isLockedRef.current) {
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      isLockedRef.current = true;
      callbackRef.current(...args);

      timeoutRef.current = setTimeout(() => {
        isLockedRef.current = false;
        timeoutRef.current = null;
      }, delay);
    },
    [delay],
  ) as T;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return throttledCallback;
};

export default useThrottledCallback;

